<template>
  <edit-modal
    :is-show-dialog="isShowDialog"
    :action-label="actionLabel"
    :title="dialogTitle"
    @on-close="onClose"
    @on-action="onAction"
  >
    <v-overlay :absolute="true" :value="isModalLoading">
      <v-progress-circular indeterminate size="24"></v-progress-circular>
    </v-overlay>
    <v-form ref="form" autocomplete="off" method="post" action>
      <input autocomplete="false" name="hidden" type="text" style="display:none;" />
      <input type="search" />
      <v-container>
        <v-row v-model="valid" :lazy-validation="true">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="editableObject.name"
              :counter="200"
              :rules="[rules.required, rules.max(200)]"
              :label="lang($store, 'sap/listings.global.filter.hints.name')"
              prepend-icon="mdi-account-badge-outline"
              required
              autocomplete="off"
              :error-messages="(errors && errors.name)?errors.name:null"
              @input="removeError('name')"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="editableObject.email"
              :counter="200"
              :rules="[rules.required, rules.max(200), rules.email]"
              prepend-icon="mdi-email"
              :label="lang($store, 'sap/listings.global.filter.hints.email')"
              type="email"
              required
              autocomplete="off"
              :error-messages="(errors && errors.email)?errors.email:null"
              @input="removeError('email')"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="editableObject.phone"
              v-mask="phoneMask"
              :rules="[rules.required, rules[initPhoneRule]]"
              :label="lang($store, 'sap/listings.global.filter.hints.phone')"
              type="tel"
              prepend-icon="mdi-phone"
              required
              autocomplete="off"
              :error-messages="(errors && errors.phone)?errors.phone:null"
              @input="removeError('phone')"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="editableObject.password"
              :counter="50"
              :rules="isCreation
              ?[rules.required, rules.max(50), rules.min(6)]
              :[rules.max(50), rules.min(6)]"
              prepend-icon="mdi-lock-question"
              :append-icon="showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              :type="showPass ? 'text' : 'password'"
              @click:append="showPass = !showPass"
              :label="lang($store, 'sap/listings.global.filter.hints.password')"
              :hint="lang($store, 'sap/listings.global.filter.hints.password_live_empty')"
              persistent-hint
              autocomplete="new-password"
              :error-messages="(errors && errors.password)?errors.password:null"
              @input="removeError('password')"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              v-model="editableObject.role"
              :items="rolesItems"
              prepend-icon="mdi-briefcase-account"
              single-line
              :hint="lang($store, 'sap/listings.global.filter.hints.role')"
              persistent-hint
              :error-messages="(errors && errors.role)?errors.role:null"
              @change="removeError('role')"
            ></v-select>
          </v-col>

          <v-col cols="12" md="6">
            <v-switch
              v-model="editableObject.active"
              class="ma-4"
              :false-value="0"
              :true-value="1"
              :label="lang($store, 'sap/listings.global.filter.hints.active')"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="editableObject.note"
              :counter="200"
              :rules="[rules.max(200)]"
              prepend-icon="mdi-tooltip-text-outline"
              :label="lang($store, 'sap/listings.global.filter.hints.note')"
              autocomplete="off"
              :error-messages="(errors && errors.note)?errors.note:null"
              @input="removeError('note')"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </edit-modal>
</template>

<script>
/* constants */
import {
  MANAGER_TOP,
  MANAGER_BASE,
  MANAGER_COMPLEX,
  MANAGER_SERVICE,
} from '../../constants/roles';
/* utils */
import { deepMerge, cloneObj, strReplaceAll } from '../../utils';
/* mixins */
import editModalMixin from '../../mixins/editModalMixin';
import validationMixin from '../../mixins/validationMixin';
import phoneMixin from "@/mixins/phoneMixin";
/* components */
import EditModal from './EditModal.vue';

export default {
  name: 'EditModalManager',
  mixins: [editModalMixin, validationMixin, phoneMixin],
  components: {
    EditModal,
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    errors: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      /* validation */
      showPass: false,
      /* Data */
      initialEditableObject: {
        name: '',
        phone: '',
        email: '',
        password: '',
        active: 1,
        role: MANAGER_BASE,
      },
      editableObject: {},
    };
  },
  mounted() {
    this.initEditableObject();
  },
  computed: {
    isCreation() {
      return !this.item.name;
    },
    dialogTitle() {
      return this.isCreation ? this.item.name : '';
    },
    rolesItems() {
      return [
        {
          text: this.lang(
            this.$store,
            'sap/listings.global.filter.roles.manager_base',
          ),
          value: MANAGER_BASE,
        },
        {
          text: this.lang(
            this.$store,
            'sap/listings.global.filter.roles.manager_top',
          ),
          value: MANAGER_TOP,
        },
        {
          text: this.lang(
            this.$store,
            'sap/listings.global.filter.roles.manager_complex',
          ),
          value: MANAGER_COMPLEX,
        },
        {
          text: this.lang(
            this.$store,
            'sap/listings.global.filter.roles.manager_service',
          ),
          value: MANAGER_SERVICE,
        },
      ];
    },
  },
  watch: {
    isShowDialog(newVal) {
      if (newVal) {
        this.initEditableObject();
        this.resetValidation();
      }
    },
  },
  methods: {
    initEditableObject() {
      this.editableObject = deepMerge(
        cloneObj(this.initialEditableObject),
        this.item,
      );
    },
    resetValidation() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    onAction() {
      if (this.$refs.form.validate()) {
        this.$emit('on-action', this.editableObject);
      }
    },
    removeError(key) {
      if (this.errors && this.errors[key]) {
        this.errors[key] = null;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
